import { memo, type ReactNode } from 'react';
import Alert, { type AlertColor } from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LightbulbRoundedIcon from '@mui/icons-material/LightbulbRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ReportRoundedIcon from '@mui/icons-material/ReportRounded';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import type { CalloutProps } from '../../types';

interface CalloutStyle {
  label: string;
  severity: AlertColor;
  icon: ReactNode;
  accent: (theme: Theme) => string;
}

const STYLES: Record<string, CalloutStyle> = {
  note: {
    label: 'Note',
    severity: 'info',
    icon: <InfoRoundedIcon fontSize="small" />,
    accent: (theme) => theme.palette.info.main,
  },
  tip: {
    label: 'Tip',
    severity: 'success',
    icon: <LightbulbRoundedIcon fontSize="small" />,
    accent: (theme) => theme.palette.success.main,
  },
  important: {
    label: 'Important',
    severity: 'info',
    icon: <CampaignRoundedIcon fontSize="small" />,
    accent: (theme) => theme.palette.primary.main,
  },
  warning: {
    label: 'Warning',
    severity: 'warning',
    icon: <WarningAmberRoundedIcon fontSize="small" />,
    accent: (theme) => theme.palette.warning.main,
  },
  caution: {
    label: 'Caution',
    severity: 'error',
    icon: <ReportRoundedIcon fontSize="small" />,
    accent: (theme) => theme.palette.error.main,
  },
};

const FALLBACK: CalloutStyle = {
  label: 'Note',
  severity: 'info',
  icon: <InfoRoundedIcon fontSize="small" />,
  accent: (theme) => theme.palette.text.secondary,
};

/**
 * Renders a GitHub-style alert. Unrecognised types fall back to neutral styling
 * with the original label preserved, so `> [!SOMETHING]` still reads sensibly.
 */
export const Callout = memo(function Callout({ type, title, children }: CalloutProps) {
  const theme = useTheme();
  const key = (type ?? '').toLowerCase();
  const known = Object.prototype.hasOwnProperty.call(STYLES, key);
  const style = known ? STYLES[key] : FALLBACK;
  const accent = style.accent(theme);
  const heading = title ?? (known ? style.label : titleCase(type));

  return (
    <Alert
      severity={style.severity}
      variant="outlined"
      icon={<Box sx={{ color: accent, display: 'flex', mt: '2px' }}>{style.icon}</Box>}
      className={`callout callout-${known ? key : 'default'}`}
      data-callout={key}
      sx={{
        my: 2.5,
        borderColor: alpha(accent, 0.4),
        borderLeft: `4px solid ${accent}`,
        borderRadius: `${theme.shape.borderRadius}px`,
        backgroundColor: alpha(accent, theme.palette.mode === 'light' ? 0.05 : 0.1),
        color: theme.palette.text.primary,
        '& .MuiAlert-message': { width: '100%', py: 0.25, overflow: 'visible' },
        '& > :last-child': { mb: 0 },
        '& p:last-of-type': { mb: 0 },
      }}
    >
      <AlertTitle
        sx={{
          color: accent,
          fontWeight: 700,
          fontSize: '0.82rem',
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          mb: 0.5,
        }}
      >
        {heading}
      </AlertTitle>
      {children}
    </Alert>
  );
});

const titleCase = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : 'Note';

export default Callout;
